import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {List} from 'immutable';

import {getModelForResourceType} from 'resources/modelRegistry';

import {pluralize} from 'lib/stringUtils';

import constants from 'client/constants';

import {Anchor, Button, Card, Heading, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import {getSchoolAccountsQuery} from 'client/SchoolAdministration/School/SchoolAccounts/shared';

import {downloadCSVTemplate} from '../shared';
import useCreateAccounts from '../useCreateAccounts';
import CreateAccountsFileInput from '../CreateAccountsFileInput/CreateAccountsFileInput.react';
import CreateAccountsResultsSummary from '../CreateAccountsResultsSummary/CreateAccountsResultsSummary.react';

import './create-accounts-wizard.scss';

const handledErrors = {
  'Bulk create error: Bulk creation of students exceeds license seat count': () => (
    <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
      Your school has exceeded its licensed seat count. Please contact us at{' '}
      <Anchor href={`mailto:${constants.CONTACT.EMAIL.SUCCESS}`} inheritColor underlined>
        {constants.CONTACT.EMAIL.SUCCESS}
      </Anchor>
      .
    </Text>
  ),
  'Bulk create error: School does not have active license': () => (
    <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
      Your school does not have an active license. Please contact us at{' '}
      <Anchor href={`mailto:${constants.CONTACT.EMAIL.SUCCESS}`} inheritColor underlined>
        {constants.CONTACT.EMAIL.SUCCESS}
      </Anchor>
      .
    </Text>
  ),
  'Bulk create error: School does not have email domain': () => (
    <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
      Your school currently does not own any email domains. Please contact us at{' '}
      <Anchor href={`mailto:${constants.CONTACT.EMAIL.SUCCESS}`} inheritColor underlined>
        {constants.CONTACT.EMAIL.SUCCESS}
      </Anchor>{' '}
      to claim an email domain for your users.
    </Text>
  ),
  'cannot create user with same email as coppa consent email':
    'One of the entries you submitted includes your own email address. Please remove the duplicate email and try again.'
};

const CreateAccountsHelpLink = () => (
  <div className='u-text-align_center u-mar-t_4'>
    <Anchor
      as='a'
      href='http://help.albert.io/en/articles/4433539-faqs-about-school-created-accounts'
      rel='noopener noreferrer'
      target='_blank'
      underlined
    >
      Learn more about CSV formatting and requirements
    </Anchor>
  </div>
);

const CreateAccountsWizard = ({coppaConsentGrantorEmail, emailDomains, school}) => {
  const [records, setRecords] = useState(List());
  const [queue, setQueue] = useState(List());

  const callback = useCallback(() => {
    getSchoolAccountsQuery(school.getId()).invalidateInterest();
  }, [school]);

  const {archivedInvalidEntries, clearUseCreateAccounts, error, validEntries, isPending, response} =
    useCreateAccounts(queue, {
      callback,
      coppaConsentGrantorEmail,
      emailDomains,
      school
    });

  const handleUpload = useCallback((payload) => setRecords(payload), [setRecords]);
  // validateRecords
  // runs offline check
  if (isPending || (!error && !response)) {
    return (
      <div className='u-position_relative' inert={isPending ? 'true' : null}>
        <Heading as='h1' className='u-mar-b_4 u-text-align_center' size='m'>
          Create Student Accounts
        </Heading>
        <Text as='div' className='u-text-align_center u-mar-b_4'>
          Get started by{' '}
          <Anchor
            as='a'
            onClick={downloadCSVTemplate}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                downloadCSVTemplate();
              }
            }}
            role='button'
            tabIndex={0}
            underlined
          >
            downloading our CSV template
          </Anchor>
          .
        </Text>
        <Card paddingLevel={3}>
          <CreateAccountsFileInput className='u-width_100pc' onChange={handleUpload} />
          {!records.isEmpty() && (
            <Button
              className='u-mar-t_5 u-width_100pc'
              disabled={records.isEmpty()}
              onClick={() => setQueue(records)}
            >
              Create student accounts
            </Button>
          )}
        </Card>
        <CreateAccountsHelpLink />
        {isPending && (
          <div className='create-accounts__pending-message u-display_flex u-flex-direction_column u-align-items_center u-justify-content_center'>
            <LoadingSpinner size={2} />
            <Heading as='h2' size='s'>
              Processing...
            </Heading>
            <Text as='p'>Please do not close this tab or your browser.</Text>
          </div>
        )}
      </div>
    );
  }

  const onTryAgain = () => {
    clearUseCreateAccounts();
    setRecords(List());
    setQueue(List());
  };

  const errorMessage = error?.response?.body.errors[0].detail;
  if (handledErrors[errorMessage]) {
    return (
      <>
        <Heading as='h1' className='u-mar-b_4 u-text-align_center' size='m'>
          Something went wrong...
        </Heading>
        <Card className='u-text-align_center' paddingLevel={3}>
          <Icon
            className='create-accounts__confirmation-icon u-mar-b_5'
            color='danger'
            icon='exclamation-triangle'
            iconStyle='light'
          />
          <div className='create-accounts__confirmation-message'>
            <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
              No accounts were created.
            </Text>
            {typeof handledErrors[errorMessage] === 'function' ? (
              handledErrors[errorMessage]()
            ) : (
              <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
                {handledErrors[errorMessage]}
              </Text>
            )}
          </div>
          <Button className='u-width_100pc' color='secondary' onClick={() => onTryAgain()}>
            Try again
          </Button>
        </Card>
        <CreateAccountsHelpLink />
      </>
    );
  }

  const someRecordsSucceeded = !validEntries.isEmpty();
  const someRecordsFailed = !archivedInvalidEntries.isEmpty();

  return (
    <>
      <Heading as='h1' className='u-mar-b_4 u-text-align_center' size='m'>
        {someRecordsSucceeded ? 'Processing your request...' : 'No accounts were created'}
      </Heading>
      <Card className='u-text-align_center' paddingLevel={3}>
        {someRecordsSucceeded && !someRecordsFailed && (
          <Icon
            className='create-accounts__confirmation-icon u-mar-b_5'
            icon='clock'
            iconStyle='light'
          />
        )}
        {someRecordsSucceeded && someRecordsFailed && (
          <Icon
            className='create-accounts__confirmation-icon u-mar-b_5'
            icon='exclamation-circle'
            iconStyle='light'
          />
        )}
        {!someRecordsSucceeded && someRecordsFailed && (
          <Icon
            className='create-accounts__confirmation-icon u-mar-b_5'
            color='danger'
            icon='exclamation-triangle'
            iconStyle='light'
          />
        )}
        <div className='create-accounts__confirmation-message'>
          {someRecordsSucceeded && (
            <Text as='p' className='create-accounts__confirmation-message__block'>
              We will send you an email once your request has finished processing. Please allow up
              to <strong>15 minutes</strong> for all accounts to be created.
            </Text>
          )}
          {someRecordsSucceeded && someRecordsFailed && (
            <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
              {`We found ${archivedInvalidEntries.size} ${pluralize(
                'record',
                archivedInvalidEntries.size
              )} that could not be processed.`}
              <br />
              Download the CSV below to view and correct any issues.
            </Text>
          )}
          {!someRecordsSucceeded && someRecordsFailed && (
            <Text as='p' className='create-accounts__confirmation-message__block' color='red'>
              No records were processed. Download the CSV below to view and correct any issues.
            </Text>
          )}
        </div>
        <CreateAccountsResultsSummary
          archivedInvalidEntries={archivedInvalidEntries}
          school={school}
          validEntries={validEntries}
        />
        {!someRecordsSucceeded && someRecordsFailed && (
          <Button className='u-width_100pc' color='secondary' onClick={() => onTryAgain()}>
            Try again
          </Button>
        )}
      </Card>
      <CreateAccountsHelpLink />
    </>
  );
};

CreateAccountsWizard.propTypes = {
  coppaConsentGrantorEmail: PropTypes.string.isRequired,
  emailDomains: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(getModelForResourceType('email_domain_v1'))
  ),
  school: PropTypes.instanceOf(getModelForResourceType('school_v5')).isRequired
};

export default CreateAccountsWizard;
